// src/features/crypto-ranking/components/FactorRadar.tsx
//
// Top-coin factor radar. The crypto universe has no category partition
// (unlike commodities' Energy / Metals / ... radar), so here each spoke is
// one of the top-ranked coins and the three overlaid series are that
// coin's component z-scores: Momentum / Volatility (inverted — higher is
// lower realised vol) / Liquidity. This lets a user see, at a glance,
// WHICH factor is carrying each leader's Forge Ultra Signal (e.g. BTC
// dominated by liquidity, a smaller alt carrying momentum). Structural
// sibling of features/commodities-ranking/components/CategoryRadar.

import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import { baseAsset, type UniverseRow } from '../api';

interface FactorRadarProps {
    data: UniverseRow[];
    /** Number of top-ranked coins to plot as spokes. */
    topN?: number;
}

// Recharts wants one object per spoke with a key per series.
interface RadarDatum {
    coin: string;
    Momentum: number;
    Volatility: number;
    Liquidity: number;
}

const RadarTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: <span className="font-mono">{p.value.toFixed(2)}</span>
                </p>
            ))}
        </div>
    );
};

export const FactorRadar = ({ data, topN = 8 }: FactorRadarProps) => {
    if (!data || data.length === 0) {
        return (
            <p className="muted text-sm">No ranked coins available yet.</p>
        );
    }

    const data_: RadarDatum[] = [...data]
        .sort((a, b) => a.universe_rank - b.universe_rank)
        .slice(0, topN)
        .map((c) => ({
            coin: baseAsset(c.symbol),
            Momentum: Number(c.momentum_z_score.toFixed(3)),
            Volatility: Number(c.volatility_z_score.toFixed(3)),
            Liquidity: Number(c.liquidity_z_score.toFixed(3)),
        }));

    return (
        <div
            className="w-full h-96 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Component factor z-scores for the top-ranked coins"
        >
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data_} outerRadius="72%">
                    <PolarGrid className="stroke-slate-200 dark:stroke-slate-700" />
                    <PolarAngleAxis
                        dataKey="coin"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        tick={{ fontSize: 10, fill: 'currentColor' }}
                    />
                    <Radar
                        name="Momentum"
                        dataKey="Momentum"
                        stroke="#f7931a"
                        fill="#f7931a"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Volatility"
                        dataKey="Volatility"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Liquidity"
                        dataKey="Liquidity"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.15}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip content={<RadarTooltip />} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default FactorRadar;
